import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Button, Dialog, FieldLabelText, IMessageProps } from '../../../Core';
import { withErrorBoundary } from '../../../Core/ErrorBoundary';
import { withInjectedProps } from '../../../Core/InjectedPropsContext';
import { getMessagesForPath } from 'Components/Worksheets/Core/Helpers/messageHelpers';

import { IWithWorksheetInjectedProps } from 'Components/Worksheets/Core';
import { Message } from 'Components/Core';
import { hasEmptyValue, hasNonEmptyValue } from 'Lib/Any';
import deepEquals from 'Lib/Object/deepEquals';
import { getHighestMessageSeverity } from '../../../Core/Message/helpers';
import { Severity } from 'Data/ViewModels/Core/Severity';
import { IconError, IconInfo, IconWarning } from 'Lib/Icons';
import { MessageSet, Table } from 'Components/Layout';
import { pipe } from 'Lib/Hooks/Selectors';
import { IconBaseProps } from 'react-icons';
import './WorksheetCollatedMessagesButton.scss';

type WorksheetCollatedMessagesButtonProps = IWithWorksheetInjectedProps & {
  /**
   * Paths to the fields to show messages for within this dialog
   */
  messagePaths: Array<string>;
  title?: string;
  iconSize?: IconBaseProps['size'];
};

const getButtonProps = (severity: Severity) => {
  switch (severity) {
    case 'error':
      return { error: true };
    case 'warning':
      return { warning: true };
    case 'info':
    default:
      return {};
  }
};

const getButtonIcon = (severity: Severity) => {
  switch (severity) {
    case 'error':
      return IconError;
    case 'warning':
      return IconWarning;
    case 'info':
    default:
      return IconInfo;
  }
};

/**
 * Button that shows a dialog with messages collated from a number of different paths
 */
let WorksheetCollatedMessagesButton: React.FC<WorksheetCollatedMessagesButtonProps> = ({
  title,
  messagePaths,
  iconSize = '1.5em',
  createWorksheetSelector,
}) => {
  const [showDialog, setShowDialog] = useState(false);

  // Normally we would avoid selecting a big, complex object until the dialog is shown
  // But we need to get all the messages to work out the severity for the button anyway.
  const messagesByPath: { [name: string]: Array<IMessageProps> } = useSelector(
    pipe(createWorksheetSelector!(''), (w) =>
      Object.fromEntries(messagePaths.map((p) => [p, getMessagesForPath(w, p)]).filter(([_, v]) => hasNonEmptyValue(v)))
    ),
    deepEquals
  );
  const titleFromWorksheet = useSelector(
    pipe(
      createWorksheetSelector!(''),
      (w) =>
        `Messages for ${w.header?.Sample?.SampleId} ${
          w.header?.TopLevelTest?.WorksheetType?.ShortName ?? w.type?.ShortName
        }`
    )
  );
  if (hasEmptyValue(messagesByPath)) return null;

  const severity = getHighestMessageSeverity(Object.values(messagesByPath).flat());

  return (
    <>
      <Button {...getButtonProps(severity)} subtle noPadding alwaysEnabled onClick={() => setShowDialog(true)}>
        {React.createElement(getButtonIcon(severity), { size: iconSize })}
      </Button>
      <Dialog
        title={title ?? titleFromWorksheet}
        width="500px"
        visible={showDialog}
        onClose={() => setShowDialog(false)}
        buttons={[
          <Button key="0" alwaysEnabled onClick={() => setShowDialog(false)}>
            Close
          </Button>,
        ]}
      >
        <Table
          colHeaders={['Field', 'Messages']}
          colWidths={[150, 350]}
          data={Object.entries(messagesByPath).map(([k, messages]) => [
            <div className={'CollatedMessageTable-Cell'}>
              <FieldLabelText name={k} />
            </div>,
            <div className={'CollatedMessageTable-Cell'}>
              <MessageSet>
                {messages.map((v, i) => (
                  <Message key={i} border={false} {...v} />
                ))}
              </MessageSet>
            </div>,
          ])}
          alternateRowShading={true}
        />
      </Dialog>
    </>
  );
};

WorksheetCollatedMessagesButton = withErrorBoundary(withInjectedProps(WorksheetCollatedMessagesButton));

export { WorksheetCollatedMessagesButton };
export default WorksheetCollatedMessagesButton;
